import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  TablePagination,

} from "@mui/material";
// components
import { Log, sendDataWithPost } from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import moment from "moment";
import * as Yup from "yup";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import { acquirerList } from "../../utils/constants";

// ----------------------------------------------------------------------

const defaultValue = {
  batch_date: null,
  start_datetime: moment().subtract(6, 'days'),
  end_datetime: moment()
}

const validationSchema = Yup.object().shape({
  batch_date: Yup.date().nullable().typeError("Invalid Batch Date"),
  start_datetime: Yup.date().nullable().typeError("Invalid Start Date"),
  end_datetime: Yup.date().nullable().typeError("Invalid End Date"),
}).test("date-range", "Start date cannot be greater than end date", function (values) {
  const { start_datetime, end_datetime } = values;
  if (start_datetime && end_datetime && moment(start_datetime).isAfter(end_datetime)) {
    return false;
  }
  return true;
});

const TABLE_HEAD = [
  { id: "gateway_transaction_id", label: "Gateway Txn ID" },
  { id: "acquirer_transaction_id", label: "Acquirer Txn ID" },
  { id: "acquirer_transaction_datetime", label: "Transaction Time" },
  { id: "acquirer_merchant_id", label: "Merchant ID" },
  { id: "acquirer_merchant_name", label: "Merchant Name" },
  { id: "acquirer_transaction_currency", label: "Currency" },
  { id: "acquirer_transaction_amount", label: "Amount" },
  { id: "acquirer_fee", label: "Fee" },
  { id: "acquirer_mdr", label: "MDR" },
  { id: "batch_date", label: "Batch Date" },
  { id: "gateway_transaction_type", label: "Gateway Transaction Type" },
  { id: "acquirer", label: "Acquirer" },
  { id: "source", label: "Source" },
];

export default function IngestAcquirer() {
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState({});
  const [errors, setErrors] = useState({});

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryList({
      page: newPage + 1,
      page_size: pagination.page_size,
    }, filter);
  };

  const handleChangeRowsPerPage = (event) => {
    queryList({
      page: 1,
      page_size: event.target.value,
    }, filter);
  };

  const queryList = (params, filter) => {
    loaded && setLoaded(false);
    queryPage(params, filter);
  };

  const queryPage = (params, filter) => {
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/payout/ingestor/filter_ingest_acquirer/?${queryParams}`;
    sendDataWithPost(url, filter, setTableData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = async (fParams) => {
    try {
      await validationSchema.validate(fParams, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const validationErrors = {};
      err.inner?.forEach((e) => {
        if (e.message === "Start date cannot be greater than end date") {
          validationErrors.start_datetime = e.message;
          validationErrors.end_datetime = e.message;
        } else {
          validationErrors[e.path] = e.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    const { batch_date, start_datetime, end_datetime, ...rest } = fParams;

    const newPrams = {
      ...rest,
      batch_date: batch_date && moment(batch_date).isValid() ? batch_date.format("YYYY-MM-DD") : null,
      start_transaction_date:
        start_datetime && moment(start_datetime).isValid()
          ? start_datetime.format("YYYY-MM-DD")
          : null,
      end_transaction_date:
        end_datetime && moment(end_datetime).isValid()
          ? end_datetime.format("YYYY-MM-DD")
          : null,
      acquirer: fParams.acquirer?.id,
      transaction_id: fParams.transaction_id?.trim(),
      merchant_id: fParams.merchant_id?.trim(),
      merchant_name: fParams.merchant_name?.trim(),
      acquirer_transaction_id: fParams.acquirer_transaction_id?.trim(),
      reconciled: fParams.reconciled,
    }

    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newPrams.source === 'all') delete newPrams.source;
    if (newPrams.reconciled === 'all') delete newPrams.reconciled;
    Log.info("Search Payout Ingest Gateway list", newPrams);
    setFilter(newPrams);
    queryList(newPagination, newPrams);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "daterange",
      label: "Transaction Time",
      param: ["start_datetime", "end_datetime"],
      width: 6
    },
    {
      type: "date",
      label: "Batch Date",
      param: "batch_date",
    },
    {
      type: "text",
      label: "Gateway Transaction ID",
      param: "transaction_id",
    },
    {
      type: "text",
      label: "Merchant ID",
      param: "merchant_id",
    },
    {
      type: "text",
      label: "Merchant Name",
      param: "merchant_name",
    },
    {
      type: "text",
      label: "Acquirer Transaction ID",
      param: "acquirer_transaction_id",
    },
    {
      type: "search-select",
      label: "Acquirer",
      param: "acquirer",
      options: acquirerList.map(item => ({
        id: item.id,
        label: item.name
      }))
    },
    {
      type: "select",
      label: "Source",
      param: "source",
      options: [
        { name: "All", id: "all" },
        { name: "GW2", id: "gw2" },
        { name: "GW3", id: 'gw3' },
      ]
    },
    {
      type: "select",
      label: "Reconciled Status",
      param: "reconciled",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={true}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
        errors={errors}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      gateway_transaction_id,
                      acquirer_transaction_id,
                      acquirer_transaction_datetime,
                      acquirer_merchant_id,
                      acquirer_merchant_name,
                      acquirer_transaction_amount,
                      acquirer_transaction_currency,
                      acquirer_fee,
                      acquirer_mdr,
                      acquirer,
                      source,
                      batch_date,
                      gateway_transaction_type
                    } = row;

                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={gateway_transaction_id} />
                        <CustomTableCell field={acquirer_transaction_id} />
                        <CustomTableCell field={acquirer_transaction_datetime} />
                        <CustomTableCell field={acquirer_merchant_id} />
                        <CustomTableCell field={acquirer_merchant_name} />
                        <CustomTableCell field={acquirer_transaction_currency} />
                        <CustomTableCell field={acquirer_transaction_amount} />
                        <CustomTableCell field={acquirer_fee} />
                        <CustomTableCell field={acquirer_mdr} />
                        <CustomTableCell field={batch_date} />
                        <CustomTableCell field={gateway_transaction_type} />
                        <CustomTableCell field={acquirer} />
                        <CustomTableCell field={source?.toUpperCase()} />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <TablePagination
              rowsPerPageOptions={[10, 50, 100]}
              component="div"
              count={tableData.count}
              rowsPerPage={pagination.page_size || 50}
              page={pagination.page - 1 || 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
}
